import { useMemo } from 'react'
import styled from '@emotion/styled'
import chroma from 'chroma-js'
import { MapMarkerAlt } from '@emotion-icons/fa-solid/MapMarkerAlt'
import { Colors, Shapes } from 'src/components/theme'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import {
  getCategoryColorFromCategory,
  getCategoryFromNaicsCode,
  SHOPPING_CENTER_NAICS_CODE,
} from 'src/constants/mapbox'
import { CircleImages } from 'src/v2/components/circle-images'
import { BrandLogo } from 'src/components/insights/location-visits/stats/location-logo'
import { Typography } from '@mui/material'
import { ShoppingCenterIcon } from 'src/components/insights/icons/shopping-center'
import { NearbyResults } from 'src/server/data/alphamap/search'
import { ResultItem } from 'src/v2/components/sidebar-result-list'
import {
  buildLocationResults,
  buildShoppingCenterResults,
} from 'src/views/map-explorer/sidebar/panels/results/search-result-items'
import { SidebarResultListItem } from 'src/v2/components/sidebar-result-list'
import { POI_CATEGORIES_MAP } from 'src/constants/poi-categories'

const ICON_SIZE = 24
const BrandedIconWrapper = styled.span<{ color?: string }>`
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
`
const IconWrapper = styled(BrandedIconWrapper)`
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: 50%;
  background-color: ${({ color }) => color || Colors.neutral100};
  display: flex;
  align-items: center;
  justify-content: center;
`

const RelatedResultsOverlayContent = styled(FlexRow)`
  gap: ${Shapes.spacing[2]}px;
  flex-wrap: wrap;
`

const CategorySection = styled(FlexColumn)`
  padding: ${Shapes.spacing[2]}px;
  border-radius: ${Shapes.borderRadius}px;
`

export const CircleResults = (props: {
  results: Array<NearbyResults['shoppingCenters'][number] | NearbyResults['locations'][number]>
}) => {
  const icons = useMemo(() => {
    return props.results.slice(0, 4).map((item) =>
      item.type === 'location' && item.meta.brandId ? (
        <BrandedIconWrapper>
          <BrandLogo key={item.meta.brandId + item.id} brandId={item.meta.brandId} size={ICON_SIZE} noBorder />
        </BrandedIconWrapper>
      ) : (
        <IconWrapper
          key={item.id}
          color={chroma(getCategoryFromNaicsCode(item.meta.naicsCode)?.color).brighten(0.2).hex()}>
          {item.meta.naicsCode === SHOPPING_CENTER_NAICS_CODE ? (
            <ShoppingCenterIcon size={12} color={Colors.textWhite} />
          ) : (
            <MapMarkerAlt size={12} color={Colors.textWhite} />
          )}
        </IconWrapper>
      )
    )
  }, [props.results])

  return <CircleImages size={24} imageUrls={[]} icons={icons} />
}

type ResultsGridProps = {
  mini?: boolean
  minWidth?: number
  deduped: {
    shoppingCenters: NearbyResults['shoppingCenters']
    locations: NearbyResults['locations']
  }
  onClick: (item: SidebarResultListItem) => void
}
export const ResultsGrid = (props: ResultsGridProps) => {
  const { deduped, onClick, mini = true, minWidth } = props
  const locationsByCategory = useMemo(() => {
    if (!deduped || (deduped.shoppingCenters.length === 0 && deduped.locations.length === 0)) {
      return []
    }
    const categories = new Set(deduped.locations.map((l) => getCategoryFromNaicsCode(l.meta.naicsCode)?.category))
    const ordered = Array.from(POI_CATEGORIES_MAP.entries())
    const categoryOrder = Object.fromEntries(ordered.map(([key], index) => [key, index]))

    return Array.from(categories)
      .sort((a, b) => {
        const aCategory = a.toUpperCase()
        const bCategory = b.toUpperCase()
        const aIndex = categoryOrder[aCategory] !== undefined ? categoryOrder[aCategory] : Infinity
        const bIndex = categoryOrder[bCategory] !== undefined ? categoryOrder[bCategory] : Infinity
        return aIndex - bIndex || a.localeCompare(b)
      })
      .map((category) => {
        return {
          category,
          locations: buildLocationResults(
            deduped.locations.filter((l) => getCategoryFromNaicsCode(l.meta.naicsCode)?.category === category),
            { sort: true }
          ),
        }
      })
  }, [deduped])

  return (
    <FlexColumn gap={Shapes.spacing[2]}>
      {deduped.shoppingCenters.length > 0 && (
        <CategorySection
          gap={Shapes.spacing[1]}
          style={{ backgroundColor: chroma(getCategoryColorFromCategory('Shopping Center')).alpha(0.03).hex() }}>
          <Typography variant="text1m" color={Colors.neutral900}>
            Shopping Centers
          </Typography>
          <RelatedResultsOverlayContent>
            {buildShoppingCenterResults(deduped.shoppingCenters).map((shoppingCenter) => (
              <ResultItem
                key={shoppingCenter.id}
                item={shoppingCenter}
                onClick={() => onClick(shoppingCenter)}
                mini={mini}
                maxWidth={minWidth}
              />
            ))}
          </RelatedResultsOverlayContent>
        </CategorySection>
      )}
      {locationsByCategory.map((category) => (
        <CategorySection
          key={category.category}
          gap={Shapes.spacing[1]}
          style={{ backgroundColor: chroma(getCategoryColorFromCategory(category.category)).alpha(0.03).hex() }}>
          <Typography variant="text1m" color={Colors.neutral900}>
            {category.category}
          </Typography>
          <RelatedResultsOverlayContent>
            {category.locations.map((location) => (
              <ResultItem
                key={location.id}
                item={location}
                onClick={() => onClick(location)}
                mini={mini}
                maxWidth={minWidth}
              />
            ))}
          </RelatedResultsOverlayContent>
        </CategorySection>
      ))}
    </FlexColumn>
  )
}
